import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { ShrinkContent } from '../ShrinkContent';
import ItemsSummaryList from './ItemsSummaryList';
import { ASSIGNMENTS, CATEGORIES } from '../../constants/operations';
import { asCurrency } from '../../utils/formatters';
import { useDataTransforms, useFilterYears } from '../../utils/hooks';
import { avg, sum } from '../../utils/aggregators';
import { useFilterContext } from '../../contexts/FilterContext';

const options = CATEGORIES.map((category) => ({
  key: category,
  value: category,
  name: _.capitalize(category),
}));

const listItems = [
  {
    name: 'Rental or Contracted Services',
    accessor: 'contractOrRentCostByYear',
  },
  {
    name: 'Depreciation',
    accessor: 'depreciationCostByYear',
  },
  {
    name: 'Developer Services',
    accessor: 'developerCostByYear',
  },
  {
    name: 'Fuel',
    accessor: 'fuelCostByYear',
  },
  {
    name: 'Materials',
    accessor: 'inputCostByYear',
  },
  {
    name: 'Labor',
    accessor: 'laborCostByYear',
  },
];

const getOperationsByCategory = (cropAggregates) =>
  _.groupBy(
    cropAggregates.flatMap(({ operationAggregates }) => operationAggregates),
    'operation.category',
  );

const OperationsItemsSummary = ({ cropAggregates, className }) => {
  const filterYears = useFilterYears();
  const { years } = useFilterContext().state;
  const [selectedOption, setSelectedOption] = useState(options[0]);

  const [operationsByCategory = []] = useDataTransforms(cropAggregates, [
    getOperationsByCategory,
  ]);

  const items = useMemo(
    () =>
      listItems.map(({ name, accessor }) => ({
        id: name,
        name,
        // For each operation category, add a property to the item with the
        // value summed across operations
        // { name: 'Materials', installation: 100, management: 1000, harvest: 200, marketing: 0 }
        ..._.fromPairs(
          Object.entries(operationsByCategory).map(([category, operations]) => {
            const aggregator = category === 'installation' ? sum : avg;
            // filter operations to only include categorical numbers for owner operations
            // or the "developer services" category
            // this is needed to make the superadmin interface consistent with the UI
            // for users with less permissions
            return [
              category,
              _.sumBy(
                operations.filter(
                  (op) =>
                    op.operation.assignment === ASSIGNMENTS.OWNER ||
                    accessor === 'developerCostByYear',
                ),
                (operation) => aggregator(filterYears(operation[accessor])),
              ),
            ];
          }),
        ),
      })),
    [filterYears, operationsByCategory],
  );

  const filteredItems = useMemo(
    () =>
      _.orderBy(
        items.filter((item) => item[selectedOption.value] >= 1),
        selectedOption.value,
        'desc',
      ),
    [items, selectedOption.value],
  );

  const valueColumn = {
    accessor: selectedOption.value,
    format: asCurrency,
  };

  const dropdownProps = {
    options,
    selectedOption: selectedOption.name,
    setOption: setSelectedOption,
  };

  return (
    <ShrinkContent className={className}>
      <ItemsSummaryList
        items={filteredItems}
        valueColumn={valueColumn}
        dropdownProps={dropdownProps}
        aggregationInfo={
          selectedOption.value === 'installation'
            ? 'Total'
            : `${years} Year Average`
        }
        viewMoreLink={`../operations?category=${selectedOption.value}`}
      />
    </ShrinkContent>
  );
};

export default OperationsItemsSummary;
